import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { useState, type MouseEvent, type ReactNode } from "react";
import { Button, Checkbox, Form, Tooltip, theme, type CheckboxProps, type NamePath } from "../ui";
import { spacing, typography } from "../settings/settingsListDesign";
import { useFormSectionCollapseStore } from "./formSectionCollapse";
import "./formSectionHeading.css";

export interface FormSectionCheckbox {
  /** Accessible label; the section title already supplies the visible caption. */
  label: string;
  /** Bind directly to the surrounding form, or supply checked/onChange for external state. */
  formName?: NamePath;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  onChange?: CheckboxProps["onChange"];
  tooltip?: ReactNode;
}

export interface FormSectionHeadingProps {
  title: ReactNode;
  /** Stabile Kennung der Stelle; macht den Abschnitt einklappbar und ist der Speicherschlüssel je Konto. */
  sectionKey?: string;
  children?: ReactNode;
  first?: boolean;
  checkbox?: FormSectionCheckbox;
  /** false keeps the content visible, even when a saved section key exists. */
  collapsible?: boolean;
  /** Zustand, solange der Nutzer an dieser Stelle noch nicht umgeschaltet hat. */
  defaultCollapsed?: boolean;
  /** Ausnahme: Zustand vom Aufrufer statt aus dem Kontospeicher (Lint `form-section-collapse`). */
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  expandLabel?: string;
  collapseLabel?: string;
}

/** One form-section renderer. Hidden fields stay mounted and participate in validation.
 * The collapse state is stored per account and section through `FormSectionCollapseProvider`.
 */
export function FormSectionHeading({ title, sectionKey, children, first, checkbox, collapsible = true, defaultCollapsed = false,
  collapsed: controlledCollapsed, onCollapsedChange, expandLabel, collapseLabel }: FormSectionHeadingProps) {
  const store = useFormSectionCollapseStore();
  const { token } = theme.useToken();
  const [localCollapsed, setLocalCollapsed] = useState(defaultCollapsed);
  const hasBody = children != null;
  const canCollapse = collapsible && !!sectionKey && hasBody;
  if (canCollapse && (!expandLabel?.trim() || !collapseLabel?.trim())) {
    throw new Error("FormSectionHeading requires expandLabel and collapseLabel for collapsible content.");
  }
  const stored = canCollapse && controlledCollapsed === undefined && store ? store.isCollapsed(sectionKey) ?? defaultCollapsed : undefined;
  const collapsed = canCollapse && (controlledCollapsed ?? stored ?? localCollapsed);
  const bodyId = sectionKey && hasBody ? `form-section-${sectionKey.replace(/[^a-zA-Z0-9_-]+/g, "-")}` : undefined;
  const toggle = () => {
    if (!canCollapse) return;
    if (controlledCollapsed === undefined && store) store.setCollapsed(sectionKey, !collapsed);
    else setLocalCollapsed(!collapsed);
    onCollapsedChange?.(!collapsed);
  };
  const stopToggle = (event: MouseEvent) => event.stopPropagation();
  const checkboxControl = checkbox ? <Checkbox aria-label={checkbox.label} checked={checkbox.checked}
    defaultChecked={checkbox.defaultChecked} disabled={checkbox.disabled} onChange={checkbox.onChange} /> : null;
  const boundCheckbox = checkbox?.formName !== undefined
    ? <Form.Item name={checkbox.formName} valuePropName="checked" noStyle>{checkboxControl}</Form.Item>
    : checkboxControl;
  const selection = checkbox?.tooltip
    ? <Tooltip title={checkbox.tooltip}><span>{boundCheckbox}</span></Tooltip> : boundCheckbox;
  const collapseButton = canCollapse ? <Button type="text" size="small" htmlType="button"
      icon={collapsed ? <DownOutlined /> : <UpOutlined />} aria-expanded={!collapsed} aria-controls={bodyId}
      aria-label={collapsed ? expandLabel : collapseLabel}
      onClick={event => { event.stopPropagation(); toggle(); }} /> : null;
  // Trennlinie für den ganzen Block (Ronny, 19.10.2026): Titel (mit Abschnittsschalter wie einem Häkchen), Linie,
  // Aufklapp-Pfeil ganz rechts. Inhalte und Aktionen des Blocks gehören in den Block, nicht auf die Linie.
  const heading = <div className="mq-form-section-heading" style={{ display: "flex", alignItems: "center", gap: spacing.sm, margin: 0 }}>
    <span style={{ display: "inline-flex", alignItems: "center", gap: spacing.sm, flex: "0 1 auto", minWidth: 0 }}>
      <span className="mq-form-section-heading-title">
      <span style={{ fontWeight: typography.title.level5.fontWeight }}>{title}</span>
      {checkbox ? <span className="mq-form-section-heading-extra" onClick={canCollapse ? stopToggle : undefined}>{selection}</span> : null}
      </span>
    </span>
    <span aria-hidden style={{ flex: "1 1 auto", minWidth: spacing.lg, borderTop: `${token.lineWidth}px ${token.lineType} ${token.colorSplit}`, alignSelf: "center" }} />
    {collapseButton}
  </div>;
  return <div className={`mq-form-section${first ? " mq-form-section-first" : ""}${hasBody ? "" : " mq-form-section-heading-only"}`}>
    {canCollapse ? <div onClick={toggle} style={{ cursor: "pointer", userSelect: "none" }}>{heading}</div> : heading}
    {hasBody && <div className="mq-form-section-body" id={bodyId} hidden={collapsed} aria-hidden={collapsed || undefined}>{children}</div>}
  </div>;
}
